import React, { useEffect, useState } from "react";
import axios from "axios";
import "./products.css";

const API_BASE = process.env.REACT_APP_API_BASE;

function getAuthToken() {
  return localStorage.getItem("access") || localStorage.getItem("access_token");
}
function calculateTotal(items) {
  if (!items || items.length === 0) return 0;
  return items.reduce(
    (sum, item) => sum + (item.product?.price || 0) * (item.quantity || 0),
    0,
  );
}
function amountsAreEqual(a, b) {
  return Math.abs(Number(a) - Number(b)) < 0.01;
}

export default function ApprovedOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [delivering, setDelivering] = useState(false);
  const [successToast, setSuccessToast] = useState(false);
  const [addPayAmount, setAddPayAmount] = useState("");

  // New state for summary popup & filters
  const [showSummary, setShowSummary] = useState(false);
  const [colorFilter, setColorFilter] = useState("");
  const [sizeFilter, setSizeFilter] = useState(""); // New state for Size Filter
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5; // number of orders per page

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError("");
      const token = getAuthToken();
      if (!token) {
        setError("You are not authenticated. Please log in.");
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(`${API_BASE}/orders/approved/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        let resultData = res.data.results || res.data;
        resultData = resultData.map((order) => ({
          ...order,
          customer_name: order.customer_name || order.user?.username || "N/A",
          customer_phone: order.customer_phone || order.user?.phone || "N/A",
          cart: order.cart || { items: order.items || [] },
          paid_amount: Number(order.paid_amount) || 0,
        }));
        setOrders(resultData);
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.clear();
          window.location.href = "/admin/login";
        } else {
          setError(
            err.response?.data?.detail || "Failed to load approved orders.",
          );
        }
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedOrder) setAddPayAmount("");
  }, [selectedOrder]);

  function calculateTotalQuantity(items) {
    if (!items || items.length === 0) return 0;
    return items.reduce((sum, item) => sum + (item.quantity || 0), 0);
  }

  const paginatedOrders = orders.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  async function handleMarkDelivered() {
    if (!selectedOrder) return;
    const token = getAuthToken();
    if (!token) {
      setError("You are not authenticated. Please log in.");
      return;
    }
    setDelivering(true);
    setError("");

    const paidAmount =
      Number(selectedOrder.paid_amount) + Number(addPayAmount || 0);

    try {
      await axios.patch(
        `${API_BASE}/orders/${selectedOrder.id}/update-status/`,
        { status: "delivered", paid_amount: paidAmount },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setOrders((prev) => prev.filter((o) => o.id !== selectedOrder.id));
      setSelectedOrder(null);
      setSuccessToast(true);
      setTimeout(() => setSuccessToast(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to mark as delivered.");
    } finally {
      setDelivering(false);
    }
  }

  // UPDATED: Filtered orders logic for both Color and Size
  const filteredOrders = orders.filter((order) => {
    return order.cart.items.some((item) => {
      const matchesColor = colorFilter
        ? item.color?.toLowerCase().includes(colorFilter.toLowerCase())
        : true;
      const matchesSize = sizeFilter
        ? item.size?.toLowerCase().includes(sizeFilter.toLowerCase())
        : true;
      return matchesColor && matchesSize;
    });
  });

  if (loading) return <div className="loading">Loading approved orders...</div>;

  return (
    <div className="orders-container">
      {error && <div className="error-message">{error}</div>}
      {successToast && (
        <div className="success-toast">
          Order marked as <strong>Delivered!</strong>
          <button
            className="toast-close-btn"
            onClick={() => setSuccessToast(false)}
          >
            ×
          </button>
        </div>
      )}
      <div className="orders-header">
        <h2>Approved Orders</h2>
        <button
          className="view-summary-btn"
          onClick={() => setShowSummary(true)}
          style={{ marginLeft: "10px", padding: "6px 12px", cursor: "pointer" }}
        >
          View Summary
        </button>
      </div>
      {orders.length === 0 ? (
        <p className="no-orders">No approved orders found.</p>
      ) : (
        <div className="table-wrapper">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Phone</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedOrders.map((order) => (
                <tr key={order.id}>
                  <td>#{order.id}</td>
                  <td>{order.customer_name}</td>
                  <td>{order.customer_phone}</td>
                  <td>
                    {order.created_at
                      ? new Date(order.created_at).toLocaleString()
                      : "N/A"}
                  </td>
                  <td>
                    <button
                      className="view-btn"
                      onClick={() => setSelectedOrder(order)}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="pagination">
        <button
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Prev
        </button>
        {Array.from({ length: Math.ceil(orders.length / pageSize) }, (_, i) => (
          <button
            key={i}
            className={currentPage === i + 1 ? "active" : ""}
            onClick={() => setCurrentPage(i + 1)}
          >
            {i + 1}
          </button>
        ))}
        <button
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={currentPage === Math.ceil(orders.length / pageSize)}
        >
          Next
        </button>
      </div>

      {/* DETAIL MODAL */}
      {selectedOrder && (
        <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div
            className="order-details-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="modal-close-btn"
              onClick={() => setSelectedOrder(null)}
            >
              ×
            </button>
            <h3>Order #{selectedOrder.id}</h3>
            <div className="order-info-grid">
              <p>
                <strong>Customer:</strong> {selectedOrder.customer_name}
              </p>
              <p>
                <strong>Phone:</strong> {selectedOrder.customer_phone}
              </p>
              <p>
                <strong>Created:</strong>{" "}
                {selectedOrder.created_at
                  ? new Date(selectedOrder.created_at).toLocaleString()
                  : "N/A"}
              </p>
            </div>
            <div className="items-section">
              <h4>Ordered Items</h4>
              {selectedOrder.cart.items.length === 0 ? (
                <p>No items.</p>
              ) : (
                selectedOrder.cart.items.map((item) => (
                  <div
                    key={item.id}
                    className="item-card"
                    style={{ display: "flex", gap: "1em", marginBottom: 10 }}
                  >
                    {item.product && item.product.product_images?.length > 0 ? (
                      <img
                        src={
                          item.product.product_images[0].image.startsWith(
                            "http",
                          )
                            ? item.product.product_images[0].image
                            : `${API_BASE.replace("/api/v1", "")}${item.product.product_images[0].image}`
                        }
                        alt="product"
                        style={{
                          width: 80,
                          height: 80,
                          objectFit: "cover",
                          borderRadius: 6,
                          background: "#efefef",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 80,
                          height: 80,
                          background: "#eee",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: 6,
                          fontSize: 12,
                          color: "#bbb",
                        }}
                      >
                        No Image
                      </div>
                    )}
                    <div style={{ flex: 1 }}>
                      <p>
                        <strong>{item.product?.name}</strong>
                      </p>
                      <p>
                        Size: {item.size || "N/A"} | Color:{" "}
                        {item.color || "N/A"}
                      </p>
                      <p>
                        Quantity: {item.quantity}
                        {item.product?.price && (
                          <> &nbsp; × Price: ETB {item.product.price}</>
                        )}
                      </p>
                      <p>
                        <strong>Subtotal:</strong> ETB{" "}
                        {(item.product?.price || 0) * (item.quantity || 0)}
                      </p>
                    </div>
                  </div>
                ))
              )}
              <hr />
              <div
                className="total-price"
                style={{ fontWeight: "bold", marginTop: 10 }}
              >
                Total Price: ETB {calculateTotal(selectedOrder.cart.items)}
              </div>
              <div style={{ margin: "5px 0" }}>
                Paid:&nbsp;
                <span style={{ color: "#279c27", fontWeight: "bold" }}>
                  ETB{" "}
                  {(
                    Number(selectedOrder.paid_amount) +
                    Number(addPayAmount || 0)
                  ).toFixed(2)}
                </span>
              </div>
              <div>
                {(() => {
                  const total = calculateTotal(selectedOrder.cart.items);
                  const paid =
                    Number(selectedOrder.paid_amount) +
                    Number(addPayAmount || 0);
                  const remaining = total - paid;
                  if (amountsAreEqual(paid, total) && total > 0) {
                    return (
                      <span
                        style={{
                          color: "#1ab322",
                          fontWeight: "bold",
                          fontSize: "1.1em",
                        }}
                      >
                        Fully Paid
                      </span>
                    );
                  }
                  return (
                    <>
                      Remaining:{" "}
                      <span style={{ color: "#e54a3d", fontWeight: "bold" }}>
                        ETB {remaining.toFixed(2)}
                      </span>
                    </>
                  );
                })()}
              </div>
              {(() => {
                const total = calculateTotal(selectedOrder.cart.items);
                const paid = Number(selectedOrder.paid_amount);
                const remaining = total - paid;
                return (
                  remaining > 0 && (
                    <div style={{ marginTop: 10 }}>
                      <label htmlFor="addPayAmountInput">
                        <strong>Add Remaining Payment (ETB):</strong>
                      </label>
                      <input
                        id="addPayAmountInput"
                        type="number"
                        min="0"
                        max={remaining}
                        step="0.01"
                        value={addPayAmount}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (
                            v === "" ||
                            (Number(v) >= 0 && Number(v) <= remaining + 0.01)
                          ) {
                            setAddPayAmount(v);
                          }
                        }}
                        style={{
                          width: 120,
                          marginLeft: 10,
                          padding: "3px 6px",
                          fontWeight: "bold",
                          borderRadius: 4,
                          border: "1px solid #ccc",
                        }}
                        placeholder={`Up to ${remaining.toFixed(2)}`}
                      />
                    </div>
                  )
                );
              })()}
            </div>
            <div className="approve-section" style={{ marginTop: 20 }}>
              <button
                className="deliver-btn"
                disabled={
                  delivering ||
                  (() => {
                    const total = calculateTotal(selectedOrder.cart.items);
                    const paid =
                      Number(selectedOrder.paid_amount) +
                      Number(addPayAmount || 0);
                    return !(amountsAreEqual(paid, total) && total > 0);
                  })()
                }
                onClick={handleMarkDelivered}
              >
                {delivering ? "Marking..." : "Mark as Delivered"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SUMMARY POPUP */}
      {showSummary && (
        <div className="modal-overlay" onClick={() => setShowSummary(false)}>
          <div
            className="summary-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxHeight: "80vh",
              overflowY: "auto",
              width: 600,
              background: "#fff",
              padding: 20,
              borderRadius: 8,
            }}
          >
            <button
              className="modal-close-btn"
              onClick={() => setShowSummary(false)}
            >
              ×
            </button>
            <h3>Approved Orders Summary</h3>
            <p>
              <strong>Total Quantity:</strong>{" "}
              {filteredOrders.reduce(
                (sum, order) => sum + calculateTotalQuantity(order.cart.items),
                0,
              )}
            </p>

            <div style={{ display: "flex", gap: "20px", margin: "10px 0" }}>
              {/* Color Filter */}
              <div>
                <label htmlFor="colorFilter">
                  <strong>Color:</strong>{" "}
                </label>
                <input
                  id="colorFilter"
                  type="text"
                  placeholder="Filter color"
                  value={colorFilter}
                  onChange={(e) => setColorFilter(e.target.value)}
                  style={{
                    padding: "3px 6px",
                    borderRadius: 4,
                    border: "1px solid #ccc",
                  }}
                />
              </div>

              {/* Size Filter - NEW SECTION */}
              <div>
                <label htmlFor="sizeFilter">
                  <strong>Size:</strong>{" "}
                </label>
                <input
                  id="sizeFilter"
                  type="text"
                  placeholder="Filter size"
                  value={sizeFilter}
                  onChange={(e) => setSizeFilter(e.target.value)}
                  style={{
                    padding: "3px 6px",
                    borderRadius: 4,
                    border: "1px solid #ccc",
                  }}
                />
              </div>
            </div>

            <div className="summary-orders-list">
              {filteredOrders.map((order) => (
                <div
                  key={order.id}
                  style={{ borderBottom: "1px solid #ccc", padding: "10px 0" }}
                >
                  <p>
                    <strong>Order #{order.id}</strong> - Customer:{" "}
                    {order.customer_name}
                  </p>
                  {order.cart.items.map((item) => (
                    <p key={item.id} style={{ marginLeft: 10 }}>
                      {item.product?.name} | Qty: {item.quantity} | Color:{" "}
                      {item.color} | Size: {item.size} | Price:{" "}
                      {item.product?.price}
                    </p>
                  ))}
                  <p>Total Price: ETB {calculateTotal(order.cart.items)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}